/* ==========================================================================
   nvBoostShowLimit
   Boost の「表示件数」を 10 / 20 / 30 / 40 / 50 にし、初期件数を
   スマホ 10件・PC 20件にする。スマホでもプルダウンを表示する。
   Change Boost's "show limit" options to 10 / 20 / 30 / 40 / 50, default to
   10 on mobile and 20 on desktop, and show the dropdown on mobile too.
   File   : assets/nv-boost-limit.js
   Loaded : layout/theme.liquid から defer で読み込む（nv-boost-nav.js の隣）
   Added  : 2026-09-17
   --------------------------------------------------------------------------
   Problem / 症状:
     Boost の選択肢は 8 / 16 / 24 / 32 / 48 で固定。管理画面（ビジュアル
     エディタ）には変更する項目が無く、URL で ?limit=30 と指定しても弾かれて
     初期件数に戻される。スマホではプルダウン自体が出ない。
     Boost hard-codes 8 / 16 / 24 / 32 / 48. The visual editor has no setting
     for it, ?limit=30 is rejected and reset, and the dropdown is hidden on
     mobile.

   Cause / 原因（Boost theme lib 2.1.17 を解析・2026-09-17）:
     1. 選択肢と URL チェックは Boost 本体 JS の定数 showLimitList を参照。
        設定（themeSettings）より本体の既定が後勝ちで、設定では上書きできない。
        Both the options and the URL check read a constant inside Boost's
        bundle; the bundle default overrides theme settings.
     2. URL チェックは初期化時だけ。リストに無い値は productsPerPage に戻す。
        URL に limit が無ければ productsPerPage がそのまま採用される。
        The check runs only at init and falls back to productsPerPage, which
        is used as-is when the URL has no limit.
     3. スマホは本体 CSS が .boost-sd__toolbar-item--show-limit-list を
        display:none にしているだけ（React は描画している）。
        On mobile the item is only hidden by CSS; React still renders it.

   How / 仕組み:
     ① URL の limit がこちらのリストに無ければ初期値に書き換える（履歴は増やさない）。
     ② Boost が起動する前に themeSettings.productList.productsPerPage を
        「URL の limit」または「スマホ 10 / PC 20」にする。Boost の初期化は
        この値をそのまま使うので、本体のリストを通さずに件数が決まる。
     ③ Boost 公式のカスタマイズ窓口（window.__BoostCustomization__ →
        useComponentPlugin('ShowLimitList')）で選択肢を差し替える。
     ④ スマホ用の CSS を 1 つ足してプルダウンを表示する。
     ⑤ 開いたリストの重なり順を上げる（お気に入りアプリの箱に負けないように）。
     ⑥ 選んだ件数を localStorage に記憶し、次回の初期値にする（2026-09-18）。
     (1) Rewrite an unknown ?limit to the default without adding history.
     (2) Before Boost boots, set productsPerPage to the URL limit or the
         device default. Boost's init uses it verbatim.
     (3) Replace the dropdown options via Boost's official plugin API.
     (4) Inject one CSS rule to un-hide the dropdown on mobile.

   Why a separate file / なぜ別ファイルにするか:
     boost-sd-custom.js は Boost 管理画面の Custom JS 欄から保存された際に
     上書きされる可能性がある。アプリが生成するファイルには手を入れない。

   Guarantees / 保証範囲:
     Boost 公式 API と設定データだけを使い、本体の内部 ID には依存しない。
     Boost が仕様を変えて効かなくなった場合は既定の 8〜48 に戻るだけで、
     ページは壊れない。
     Uses only Boost's public plugin API and config data. If Boost changes,
     the page falls back to the stock 8-48 list; nothing breaks.

   Debug / デバッグ:
     URL に ?nvdebug=1 を付けるとコンソールに動作ログが出る。
     window.NV_LIMIT_DISABLE = true を先に定義すると何もしない。

   To disable / 無効化する場合:
     layout/theme.liquid の script タグを外す。他の処理はこのファイルに
     依存しない。snippets/boost-sd-fallback.liquid の products_per_page は
     読み込み中の仮表示にだけ使われるので、合わせて戻す。
   ========================================================================== */

(function () {
  'use strict';

  if (window.NV_LIMIT_DISABLE === true) return;

  /* 選択肢。URL の limit もこのリストで判定する。
     The options. ?limit is validated against this list too. */
  var LIST = [10, 20, 30, 40, 50];

  /* 初期件数。Boost 本体 CSS と同じ 767px 以下をスマホとみなす。
     Defaults. 767px and below counts as mobile, same as Boost's CSS. */
  var DEFAULT_PC = 20;
  var DEFAULT_MOBILE = 10;
  var MOBILE_MAX_WIDTH = 767;

  var DEBUG = /[?&]nvdebug=1/.test(window.location.search) || window.NV_LIMIT_DEBUG === true;
  function log() {
    if (!DEBUG || !window.console) return;
    console.log.apply(console, ['[nv-limit]'].concat(Array.prototype.slice.call(arguments)));
  }

  function isMobile() {
    try {
      if (window.matchMedia) {
        return window.matchMedia('(max-width: ' + MOBILE_MAX_WIDTH + 'px)').matches;
      }
    } catch (err) { /* fall through */ }
    return window.innerWidth <= MOBILE_MAX_WIDTH;
  }

  /* ---------------------------------------------------------------
     ① URL の limit を確認する
        Check ?limit in the URL
     --------------------------------------------------------------- */
  /* 前回選んだ件数をブラウザに記憶する（スマホとPCは別々）。
     優先順位：URL の limit → 記憶した値 → 端末の既定。
     Remember the visitor's last choice per device. Priority: ?limit in
     the URL, then the saved value, then the device default. */
  var STORAGE_KEY = 'nv-boost-limit:' + (isMobile() ? 'mobile' : 'desktop');

  function readSaved() {
    try {
      var saved = parseInt(window.localStorage.getItem(STORAGE_KEY), 10);
      return LIST.indexOf(saved) !== -1 ? saved : null;
    } catch (err) {
      return null;
    }
  }

  function save(value) {
    try {
      window.localStorage.setItem(STORAGE_KEY, String(value));
      log('saved', value, 'as', STORAGE_KEY);
    } catch (err) {
      log('save failed', err);
    }
  }

  var initial = readSaved() || (isMobile() ? DEFAULT_MOBILE : DEFAULT_PC);

  try {
    var url = new URL(window.location.href);
    var raw = url.searchParams.get('limit');
    if (raw !== null) {
      var wanted = parseInt(raw, 10);
      if (LIST.indexOf(wanted) !== -1) {
        initial = wanted;
      } else {
        /* 旧リスト（24 や 48 など）やリスト外の値は初期値に置き換える。
           replaceState なので「戻る」の履歴は増えない。
           Old values such as 24 / 48 become the default. replaceState
           keeps the history clean. */
        url.searchParams.set('limit', String(initial));
        window.history.replaceState(window.history.state, '', url.toString());
        log('limit', raw, 'is not allowed ->', initial);
      }
    }
  } catch (err) {
    log('url check failed', err);
  }

  log('initial limit =', initial, isMobile() ? '(mobile)' : '(desktop)');

  /* プルダウンで選んだ瞬間に記憶する。Boost は React で描画するため、
     document で click を拾い、選択肢の要素なら値を保存する。
     Save the choice when an option is clicked. Boost renders with React,
     so listen on document and look for the option element. */
  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;
    var option = target.closest('.boost-sd__show-limit-list-option');
    if (!option) return;
    var chosen = parseInt(option.textContent, 10);
    if (LIST.indexOf(chosen) !== -1) save(chosen);
  }, true);

  /* ---------------------------------------------------------------
     ② Boost の設定に初期件数を入れる
        Put the default into Boost's config
     --------------------------------------------------------------- */
  function applyDefault(config) {
    if (!config || typeof config !== 'object') return false;
    try {
      config.themeSettings = config.themeSettings || {};
      config.themeSettings.productList = config.themeSettings.productList || {};
      config.themeSettings.productList.productsPerPage = initial;
      log('productsPerPage =', initial);
      return true;
    } catch (err) {
      log('applyDefault failed', err);
      return false;
    }
  }

  /* 通常は theme.liquid の defer 読み込みなので、この時点で設定は
     もう存在する。万一まだ無ければ、作られた瞬間に入れる。
     Normally the config already exists when this deferred script runs.
     If not, apply it the moment Boost assigns it. */
  if (!applyDefault(window.boostSDAppConfig)) {
    try {
      var stored = window.boostSDAppConfig;
      Object.defineProperty(window, 'boostSDAppConfig', {
        configurable: true,
        enumerable: true,
        get: function () { return stored; },
        set: function (value) { stored = value; applyDefault(value); }
      });
      log('waiting for boostSDAppConfig');
    } catch (err) {
      log('defineProperty failed', err);
    }
  }

  /* ---------------------------------------------------------------
     ③ プルダウンの選択肢を差し替える（Boost 公式 API）
        Replace the dropdown options via Boost's plugin API
     --------------------------------------------------------------- */
  var plugins = window.__BoostCustomization__;
  plugins = Array.isArray(plugins) ? plugins : (plugins ? [plugins] : []);
  window.__BoostCustomization__ = plugins.concat([
    function (registry) {
      try {
        registry.useComponentPlugin('ShowLimitList', {
          name: 'nv-show-limit-options',
          enabled: true,
          apply: function () {
            return {
              props: function (props) {
                props.itemPerPageOptions = LIST.slice();
                return props;
              }
            };
          }
        });
        log('ShowLimitList plugin registered');
      } catch (err) {
        log('plugin registration failed', err);
      }
    }
  ]);

  /* ---------------------------------------------------------------
     ④ スマホでもプルダウンを表示する
        Show the dropdown on mobile
        Boost の main.css は後から body に追加されるため、同じ強さでは
        負ける。display 等は !important で上書きする。
        Boost's main.css is appended later, so !important is required.
     --------------------------------------------------------------- */
  /* 開いたリストの重なり順。お気に入り（Growave）のハート用の箱が各商品
     カードの上端に z-index:99 で置かれ、リストの 2 番目の選択肢（PC では
     右端カードの左上と重なる）のクリックを奪っていた。100 以上で勝つ。
     Stacking order of the open list. Growave's wishlist placeholder sits on
     top of every product card at z-index:99 and was stealing the click on
     the second option (it overlaps the top-left of the last card on desktop).
     2026-09-17 実クリック試験で確認。 */
  var css =
    '.boost-sd__show-limit-list-list { z-index: 100 !important; }' +
    /* スマホ：並び替えの行（order 0）と「◯◯ products」の行（order 2）の
       間（order 1）に、左寄せで置く。開いたリストも左端に揃える。
       Mobile: put it between the sorting row (order 0) and the product
       count row (order 2), left-aligned; open the list under its left edge. */
    '@media only screen and (max-width: ' + MOBILE_MAX_WIDTH + 'px) {' +
    '  .boost-sd__toolbar-item--show-limit-list {' +
    '    display: flex !important;' +
    '    order: 1 !important;' +
    '    flex-basis: 100% !important;' +
    '    justify-content: flex-start !important;' +
    '    padding-left: var(--boostsd-general-gutter-mobile, 15px);' +
    '  }' +
    '  .boost-sd__toolbar-item--show-limit-list .boost-sd__show-limit-list-list {' +
    '    left: 0;' +
    '    right: auto;' +
    '  }' +
    '}';

  try {
    var style = document.createElement('style');
    style.id = 'nv-boost-limit-css';
    style.textContent = css;
    (document.head || document.documentElement).appendChild(style);
  } catch (err) {
    log('css injection failed', err);
  }
})();
